import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { QuickPanelKey } from '@/app/surfaces/quick-panel-registry.js';

export interface QuickPanelEntryContextValue {
  id: string;
  key: QuickPanelKey;
}

const QuickPanelEntryContext = createContext<QuickPanelEntryContextValue | null>(null);

interface QuickPanelEntryProviderProps {
  entryId: string;
  entryKey: QuickPanelKey;
  children: ReactNode;
}

/**
 * Publishes the stack identity of the drawer that renders the panel content, so the content can
 * resolve what to close by id instead of counting levels to pop (BR-IDT-45).
 */
export function QuickPanelEntryProvider({
  entryId,
  entryKey,
  children,
}: QuickPanelEntryProviderProps) {
  const value = useMemo<QuickPanelEntryContextValue>(
    () => ({ id: entryId, key: entryKey }),
    [entryId, entryKey],
  );

  return (
    <QuickPanelEntryContext.Provider value={value}>{children}</QuickPanelEntryContext.Provider>
  );
}

/** Returns null when the panel renders outside the quick panel stack (page or form drawer host). */
export function useQuickPanelEntryOptional(): QuickPanelEntryContextValue | null {
  return useContext(QuickPanelEntryContext);
}

export function useQuickPanelEntry(): QuickPanelEntryContextValue {
  const value = useQuickPanelEntryOptional();

  if (!value) {
    throw new Error('useQuickPanelEntry must be used within QuickPanelEntryProvider');
  }

  return value;
}
